<script setup lang="ts">
// 2026-08-31 fix (Issue 1): "Start match" on Home was dead-ending with the server's
// (correct) "this table already has a live match" error whenever a previous match was left
// live — the UX bug was that the app never SHOWED the existing live match, only errored when
// you tried to start a new one. This banner surfaces that state wherever it can be hit (Home,
// New match) and offers the two obvious actions: resume into it, or abandon it to free the
// table. Reads tableStore directly (already SSE-driven, DESIGN.md 9.2 "single source of
// truth") so it needs no props and stays in sync with zero plumbing; abandoning clears
// tableStore.state.liveMatch itself once the server's _publish_table_state echo comes back
// (match_service.py's abandon() flips status off 'live' before republishing), which is what
// makes this banner disappear and unblocks Start on its own — no extra bookkeeping here.
import { computed, ref } from 'vue'
import { tableStore } from '../store/table'
import { abandonMatch, ApiRequestError } from '../backend'
import { navigate } from '../store/nav'
import ConfirmDialog from './ConfirmDialog.vue'

const liveMatch = computed(() => tableStore.state?.liveMatch ?? null)
const confirmOpen = ref(false)
const abandoning = ref(false)
const error = ref<string | null>(null)

function resume() {
  if (!liveMatch.value) return
  navigate('live', { matchId: liveMatch.value.id })
}

async function onAbandon() {
  confirmOpen.value = false
  const m = liveMatch.value
  if (!m) return
  abandoning.value = true
  error.value = null
  try {
    await abandonMatch(m.id)
    // No local state mutation needed — the SSE echo (tableStore) clears liveMatch and this
    // banner unmounts itself once the server confirms.
  } catch (e) {
    error.value = e instanceof ApiRequestError ? e.message : 'No se pudo finalizar la partida'
  } finally {
    abandoning.value = false
  }
}
</script>

<template>
  <div v-if="liveMatch" class="shrink-0 rounded-2xl bg-amber-950/50 border border-amber-700 p-3 flex flex-col gap-2">
    <p class="text-sm font-semibold text-amber-200 text-center">
      Ya hay una partida en curso en esta mesa: {{ liveMatch.currentPlayers.left.displayName }} vs
      {{ liveMatch.currentPlayers.right.displayName }}
    </p>
    <div class="flex gap-2">
      <button
        type="button"
        class="tap-target flex-1 rounded-xl bg-emerald-600 active:bg-emerald-500 font-bold"
        @click="resume"
      >
        Ver partida
      </button>
      <button
        type="button"
        class="tap-target flex-1 rounded-xl bg-red-900 active:bg-red-800 font-bold text-red-200 disabled:opacity-40"
        :disabled="abandoning"
        @click="confirmOpen = true"
      >
        {{ abandoning ? 'Finalizando…' : 'Finalizar' }}
      </button>
    </div>
    <p v-if="error" class="text-xs text-red-400 font-semibold text-center">{{ error }}</p>

    <ConfirmDialog
      :open="confirmOpen"
      title="¿Abandonar esta partida?"
      body="No se registrará ningún cambio de puntuación."
      confirm-label="Abandonar"
      cancel-label="Cancelar"
      danger
      @confirm="onAbandon"
      @cancel="confirmOpen = false"
    />
  </div>
</template>
